import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import '@/assets/styles/components/dropdown.scss';

interface DropdownOption {
	id?: number | string | undefined;
	name?: string;
	disabled?: boolean;
	[key: string]: any;
}

interface DropdownProps<T extends DropdownOption> {
	options: T[];
	selectedOption: T | null;
	onOptionSelect: (option: T) => void;
	placeholder?: string;
	className?: string;
	size?: 'small' | 'medium' | 'large';
	fullWidth?: boolean;
	menuPosition?: 'bottom' | 'top';
	menuAlign?: 'left' | 'right';
	disabled?: boolean;
}

function Dropdown<T extends DropdownOption>({
	options,
	selectedOption,
	onOptionSelect,
	placeholder = 'Select an option',
	className,
	size = 'medium',
	fullWidth = false,
	menuPosition = 'bottom',
	menuAlign = 'left',
	disabled = false
}: DropdownProps<T>) {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);

	const toggleDropdown = () => {
		if (!disabled) {
			setIsOpen(!isOpen);
		}
	};

	const handleOptionSelect = (option: T) => {
		if (!option.disabled) {
			onOptionSelect(option);
			setIsOpen(false);
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent): void => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('keydown', handleEscape);
			return () => {
				document.removeEventListener('keydown', handleEscape);
			};
		}
	}, [isOpen]);

	return (
		<div
			className={clsx(
				'custom-dropdown',
				{
					[`custom-dropdown--${size}`]: size !== 'medium',
					'custom-dropdown--full-width': fullWidth,
					[`custom-dropdown--menu-${menuAlign}`]: menuAlign !== 'left',
					[`custom-dropdown--menu-${menuPosition}`]: menuPosition !== 'bottom'
				},
				className
			)}
			ref={dropdownRef}>
			<div
				className={clsx('custom-dropdown__trigger', {
					'custom-dropdown__trigger--open': isOpen,
					'custom-dropdown__trigger--disabled': disabled
				})}
				onClick={toggleDropdown}
				role="button"
				tabIndex={disabled ? -1 : 0}
				aria-haspopup="listbox"
				aria-expanded={isOpen}>
				<span
					className={clsx('custom-dropdown__label', {
						'custom-dropdown__label--placeholder': !selectedOption
					})}>
					{selectedOption?.name || placeholder}
				</span>
				<i
					className={clsx('custom-dropdown__icon icons-alt-arrow-down', {
						'custom-dropdown__icon--open': isOpen
					})}
				/>
			</div>

			{isOpen && (
				<div
					className="custom-dropdown__menu"
					role="listbox"
					aria-label="Options">
					{options.map(option => (
						<div
							key={option.id}
							className={clsx('custom-dropdown__option', {
								'custom-dropdown__option--selected': option.id === selectedOption?.id,
								'custom-dropdown__option--disabled': option.disabled
							})}
							onClick={() => handleOptionSelect(option)}
							role="option"
							aria-selected={option.id === selectedOption?.id}
							tabIndex={option.disabled ? -1 : 0}>
							{option.name}
						</div>
					))}
				</div>
			)}
		</div>
	);
}

export default Dropdown;
